import {defineStore} from 'pinia'
import {useSearchQueryStore} from "@/store/SearchQueryStore";
import {useSuggestionStore} from "@/store/AutocompletePopup/SuggestionStore";
import {config} from "@/core/config";
import {SanitizeValue} from "@/core/utils";
import {ProductInterface} from "@/core/types";
import {SmartSearchConfig} from "@/main";
import {searchErrorLogger, searchLogger} from "@/core/Logger";
import {useUserRecentQueriesStore} from "@/store/AutocompletePopup/UserRecentQueriesStore";


export const usePopupStore = defineStore('PopupStore', {
  state: () => {
    return {
      showAutocompletePopup: false,
      showResults: false,
      results: [],
      total: 0,
      typingTimer: 0,
      loading: false,
      productsController: 0,
      bestseller: [] as Array<ProductInterface>,
    }
  },
  getters: {
    hasAnyResults(state) {
      return state.results.length > 0
    },
    getTotalProducts(state): number {
      return state.total
    },
    hasNoResults(state) {
      return state.results.length === 0
    },
    canShowPromotedSection(state) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      return state.total >= SmartSearchConfig.value.autocomplete.results.count
    },
    getBestseller(state) {
      return state.bestseller
    },
    getAutocompleteResultsWithoutFirst(state) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      return state?.results?.slice(1, SmartSearchConfig.value.autocomplete.results.count + 1)
    },
    getAutocompleteResults(state) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      return state?.results?.slice(0, SmartSearchConfig.value.autocomplete.results.count)
    }
  },
  actions: {
    setBodyActiveAutocompleteClass(set: boolean) {
      if (set) {
        document.body.classList.add('q-autocomplete-active');
        document.documentElement.classList.add('q-autocomplete-active');
        
      } else {
        document.body.classList.remove('q-autocomplete-active');
        document.documentElement.classList.remove('q-autocomplete-active');
      }
    },
    showAutocomplete() {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      // if (!SmartSearchConfig.value.onfocus.showOnfocus) {
      //   return
      // }
      this.showAutocompletePopup = true;
      this.setBodyActiveAutocompleteClass(true)
    },
    hideAutocomplete() {
      this.showAutocompletePopup = false;
      this.setBodyActiveAutocompleteClass(false)
    },
    clearBestseller() {
      this.bestseller = []
    },
    handleKeyUp() {
      const queryStore = useSearchQueryStore();
      const suggestionStore = useSuggestionStore();
      
      if (queryStore.searchQuery.length <= 2) {
        suggestionStore.showSuggestions = false;
        this.showResults = false;
        return;
      }
      
      if (this.typingTimer) {
        clearTimeout(this.typingTimer);
      }
      
      
      this.typingTimer = setTimeout(async () => {
        try {
          this.loading = true;
          await Promise.all([suggestionStore.fetchNewSuggestions(), this.fetchAutocompleteResults()]);
          this.loading = false;
        } catch (e) {
          this.loading = false;
        }
      }, 400);
    },
    async fetchAutocompleteResults() {
      const queryStore = useSearchQueryStore();
      if (queryStore.searchQuery.length < 3) {
        return;
      }
      const userRecentQueriesStore = useUserRecentQueriesStore()
      userRecentQueriesStore.addQuery(queryStore.searchQuery)
      
      if (this.productsController) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        this.productsController.abort();
      }
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      this.productsController = new AbortController();
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const {signal} = this.productsController
      
      const data = new FormData();
      const sanitizedQuery = SanitizeValue(queryStore.searchQuery)
      data.append('q', sanitizedQuery);
      
      try {
        this.loading = true;
        const response = await fetch(`https://${config.apiHost}/restapi/${config.customerSymbol}/search/${config.searchId}/products`, {
          method: 'POST',
          headers: {
            'Api-key': config.apiKey,
          },
          credentials: 'include',
          body: data,
          signal,
        });
        
        if (response.ok) {
          const data = await response.json();
          this.clearBestseller()
          const {products, meta} = data;
          
          const {total} = meta
          
          this.total = total
          this.results = products;
          this.bestseller = products[0]
          
          this.showResults = true;
          this.loading = false;
        }
      } catch (error) {
        this.showResults = false;
        this.loading = false;
        searchErrorLogger.log('Error retrieving search results:', error);
      }
    },
    
    handleClickOutside(event: Event) {
      const targetClasses = event.target instanceof Element ? Array.from(event.target.classList) : [];
      
      const defaultExcludedClasses = [
        'slick-arrow', // Strzałki Slick Slider
        'swiper-button-next', // Przycisk "Next" Swiper
        'swiper-button-prev', // Przycisk "Prev" Swiper
        'glide__arrow', // Strzałki Glide.js
        'splide__arrow', // Strzałki Splide.js
        'tns-controls', // Kontrolki Tiny-slider
        'flickity-prev-next-button', // Przycisk "Prev" i "Next" Flickity
        'owl-nav', // Strzałki Owl Carousel
        'owl-dot', // Punkty nawigacyjne Owl Carousel
        'slider-nav-left',
        'slider-nav-right',
        'autocomplete-search__input',
        "q-form-control",
        "autocomplete-search__input-max-width",
        "autocomplete-popup__input-container",
        "q-input-group",
      ];
      
      const additionalExcludedClasses = window._QONizer?.customSmartSearchConfig?.excludedClasses || [];
      const allExcludedClasses = [...defaultExcludedClasses, ...additionalExcludedClasses];
      
      const isExcluded = allExcludedClasses.some(className => targetClasses.includes(className));
      
      
      const searchInput = document.getElementById('autocomplete-search__input');
      const autocompletePopup = document.getElementById('smartsearch-autocomplete__popup-container');
      
      if (!searchInput || !autocompletePopup || isExcluded) {
        return;
      }
      
      if (targetClasses.length!== 0 && !searchInput.contains(event.target as Node) && !autocompletePopup.contains(event.target as Node)) {
        searchLogger.log('Hiding Autocomplete popup', targetClasses);
        this.hideAutocomplete();
        return;
      }
    }
    
  },
})